import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Course, CourseWithChapters } from "@jelly/lesson";

import { LmsCourseProgress } from "../interfaces/LmsCourseProgress";
import { courseProvider, progressProvider } from "../providers/local";
import { CourseLessonProgress } from "../interfaces/CourseLessonProgress";
import { getCourseLessonsProgress } from "../services/Utils";

interface OverviewModalData {
  course: CourseWithChapters;
  courseProgress: LmsCourseProgress;
  courseLessonsProgress: CourseLessonProgress[];
}

export interface UseDashboardResult {
  courses: DashboardCourse[] | null;
  overviewModal: OverviewModalData | null;
  openOverviewModal(courseId: string): void;
  closeOverviewModal(): void;
}

export interface DashboardCourse {
  courseId: string;
  name: string;
  description: string;
  certificateUrl: string | null;
  progress: {
    totalProgress: number;
    correctAnswers: number;
    incorrectAnswers: number;
  };
}

function getDashboardCourses(courses?: Course[], coursesProgress?: LmsCourseProgress[]): DashboardCourse[] | null {
  if (!courses) {
    return null;
  }
  if (!coursesProgress) {
    return null;
  }

  return courses.map((course) => {
    const courseProgress = coursesProgress?.find((progress) => progress.id === course.id);

    if (!courseProgress) {
      throw Error(`No course progress for ${course.id}`);
    }

    return {
      courseId: course.id,
      name: course.name,
      description: course.description,
      certificateUrl: courseProgress.certificateUrl,
      progress: courseProgress.progress,
    };
  });
}

export default function useDashboardController(): UseDashboardResult {
  const [overviewModal, setOverviewModal] = useState<OverviewModalData | null>(null);
  const [overviewModalCourseId, setOverviewModalCourseId] = useState<string | null>(null);

  const queryCourses = useQuery(["queryCourses"], () => courseProvider.getAll(), {
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });
  const queryCoursesProgress = useQuery(["queryCoursesProgress"], () => progressProvider.getCoursesProgress(), {
    staleTime: 10 * 60 * 1000,
    keepPreviousData: true,
  });

  const { data: queryModalOverviewData } = useQuery(
    ["queryOverviewModal", overviewModalCourseId],
    async () => {
      const courseId = overviewModalCourseId as string;

      const course = await courseProvider.get(courseId);
      const lessonResults = await progressProvider.getCourseLessonResults(courseId);
      const courseProgress = await progressProvider.getCourseProgress(courseId);

      const courseLessonsProgress = getCourseLessonsProgress(course, lessonResults, courseProgress);

      return {
        course,
        courseProgress,
        courseLessonsProgress,
      };
    },
    {
      staleTime: 10 * 60 * 1000,
      enabled: overviewModalCourseId !== null,
    }
  );

  useEffect(() => {
    // We need to react on data from useQuery in this way because on second modal open we have data in cache and onSuccess function won't be called in useQuery.
    setOverviewModal(queryModalOverviewData ?? null);
  }, [queryModalOverviewData]);

  function openOverviewModal(courseId: string): void {
    setOverviewModalCourseId(courseId);
  }

  function closeOverviewModal(): void {
    setOverviewModalCourseId(null);
  }

  return {
    courses: getDashboardCourses(queryCourses.data, queryCoursesProgress.data),
    overviewModal,
    openOverviewModal,
    closeOverviewModal,
  };
}
